import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Stack,
  Paper,
  Table,
  Badge,
  Button,
  Group,
  Box,
  ActionIcon,
  Tooltip,
  Alert,
  Skeleton,
  TextInput,
  Select
} from '@mantine/core';
import {
  IconPlus,
  IconEye,
  IconEdit,
  IconTrash,
  IconUser,
  IconCircleCheck,
  IconCircleX,
  IconAlertTriangle,
  IconSearch,
  IconBuildingStore
} from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { useUsersQuery } from '../../../application/queries/userQueries';
import { UserListItemDto } from '../../../application/dto/UserDtos';
import { CreateUserModal } from '../../components/users/CreateUserModal';
import { EditUserModal } from '../../components/users/EditUserModal';
import { DeleteUserModal } from '../../components/users/DeleteUserModal';

/**
 * UsersPage - Admin-only page for managing users
 *
 * Features:
 * - List all users with details
 * - Search and filter users
 * - Create new user
 * - Edit existing user
 * - Delete user
 * - View user details
 *
 * Security:
 * - Admin-only access enforced by route protection
 * - Backend enforces all authorization
 */
export const UsersPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserListItemDto | null>(null);
  const [selectedUserIdForEdit, setSelectedUserIdForEdit] = useState<string | null>(null);

  // Fetch users using React Query
  const { data, isLoading, error } = useUsersQuery(
    { pageNumber: 1, pageSize: 100 }, // TODO: Add pagination later
    user?.role,
    true
  );

  const users = data?.items ?? [];

  /**
   * Filter users based on search query, role filter, and status filter
   */
  const filteredUsers = users.filter((userItem) => {
    // Search filter
    const matchesSearch =
      !searchQuery ||
      userItem.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      userItem.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      userItem.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      userItem.businessName?.toLowerCase().includes(searchQuery.toLowerCase());

    // Role filter
    const matchesRole = !roleFilter || userItem.roles.includes(roleFilter);

    // Status filter
    let matchesStatus = true;
    if (statusFilter === 'active') {
      matchesStatus = userItem.isActive;
    } else if (statusFilter === 'inactive') {
      matchesStatus = !userItem.isActive;
    } else if (statusFilter === 'email-confirmed') {
      matchesStatus = userItem.emailConfirmed;
    } else if (statusFilter === 'email-unconfirmed') {
      matchesStatus = !userItem.emailConfirmed;
    }

    return matchesSearch && matchesRole && matchesStatus;
  });

  /**
   * Navigation handlers
   */
  const handleViewUser = useCallback(
    (userItem: UserListItemDto): void => {
      navigate(`/admin/users/${userItem.id}`);
    },
    [navigate]
  );

  /**
   * Open create user modal
   */
  const handleCreateUser = useCallback((): void => {
    setIsCreateModalOpen(true);
  }, []);

  /**
   * Open edit user modal
   * Stores user ID for fetch-on-demand
   */
  const handleEditUser = useCallback((userItem: UserListItemDto): void => {
    setSelectedUserIdForEdit(userItem.id);
    setIsEditModalOpen(true);
  }, []);

  /**
   * Open delete user modal
   */
  const handleDeleteUser = useCallback((userItem: UserListItemDto): void => {
    setSelectedUser(userItem);
    setIsDeleteModalOpen(true);
  }, []);

  /**
   * Handle search input change
   */
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(event.currentTarget.value);
  }, []);

  /**
   * Render loading skeleton
   */
  if (isLoading && users.length === 0) {
    return (
      <Stack gap="lg">
        <Group justify="space-between">
          <Box>
            <Skeleton height={36} width={200} mb="xs" />
            <Skeleton height={20} width={300} />
          </Box>
          <Skeleton height={36} width={120} />
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Stack gap="xs">
            <Skeleton height={40} />
            <Skeleton height={60} />
            <Skeleton height={60} />
            <Skeleton height={60} />
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={1} ff="Comfortaa, sans-serif">
            Users
          </Title>
          <Text c="dimmed" size="lg" mt="xs">
            Manage users and their permissions
          </Text>
        </Box>

        <Button leftSection={<IconPlus size={16} />} onClick={handleCreateUser}>
          Create User
        </Button>
      </Group>

      {/* Search and Filters */}
      {users.length > 0 && (
        <Group>
          <TextInput
            placeholder="Search users by name, email, or business..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={handleSearchChange}
            size="md"
            style={{ flex: 1 }}
          />

          <Select
            placeholder="Filter by role"
            data={[
              { value: '', label: 'All Roles' },
              { value: 'Admin', label: 'Admin' },
              { value: 'BusinessOwner', label: 'Business Owner' }
            ]}
            value={roleFilter}
            onChange={setRoleFilter}
            size="md"
            clearable
            w={180}
          />

          <Select
            placeholder="Filter by status"
            data={[
              { value: '', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'email-confirmed', label: 'Email Confirmed' },
              { value: 'email-unconfirmed', label: 'Email Unconfirmed' }
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            size="md"
            clearable
            w={200}
          />
        </Group>
      )}

      {/* Error Alert */}
      {error && (
        <Alert icon={<IconAlertTriangle />} title="Error Loading Users" color="red" variant="light">
          {error.message}
        </Alert>
      )}

      {/* Users Table */}
      {!error && filteredUsers.length > 0 && (
        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Business</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Last Login</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredUsers.map((userItem) => (
                <Table.Tr key={userItem.id}>
                  {/* User Info */}
                  <Table.Td>
                    <Group gap="xs">
                      <IconUser size={16} color="var(--mantine-color-gray-6)" />
                      <Box>
                        <Text fw={600}>{userItem.fullName || userItem.userName}</Text>
                        {userItem.fullName && (
                          <Text size="xs" c="dimmed">
                            @{userItem.userName}
                          </Text>
                        )}
                      </Box>
                    </Group>
                  </Table.Td>

                  {/* Email */}
                  <Table.Td>
                    <Box>
                      <Text size="sm">{userItem.email}</Text>
                      {userItem.emailConfirmed && (
                        <Badge size="xs" color="green" variant="light" mt={4}>
                          Verified
                        </Badge>
                      )}
                    </Box>
                  </Table.Td>

                  {/* Role */}
                  <Table.Td>
                    {userItem.roles.map((role) => (
                      <Badge key={role} variant="light" color={role === 'Admin' ? 'red' : 'blue'} mr={4}>
                        {role}
                      </Badge>
                    ))}
                  </Table.Td>

                  {/* Business */}
                  <Table.Td>
                    {userItem.businessName ? (
                      <Group gap="xs">
                        <IconBuildingStore size={14} />
                        <Text size="sm">{userItem.businessName}</Text>
                      </Group>
                    ) : (
                      <Text size="sm" c="dimmed">
                        No business
                      </Text>
                    )}
                  </Table.Td>

                  {/* Status */}
                  <Table.Td>
                    {userItem.isActive ? (
                      <Badge color="green" variant="light" leftSection={<IconCircleCheck size={14} />}>
                        Active
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" leftSection={<IconCircleX size={14} />}>
                        Inactive
                      </Badge>
                    )}
                  </Table.Td>

                  {/* Last Login */}
                  <Table.Td>
                    {userItem.lastLoginAt ? (
                      <Text size="sm" c="dimmed">
                        {new Date(userItem.lastLoginAt).toLocaleDateString()}
                      </Text>
                    ) : (
                      <Text size="sm" c="dimmed">
                        Never
                      </Text>
                    )}
                  </Table.Td>

                  {/* Actions */}
                  <Table.Td>
                    <Group gap="xs">
                      <Tooltip label="View Details">
                        <ActionIcon variant="light" color="blue" onClick={() => handleViewUser(userItem)}>
                          <IconEye size={18} />
                        </ActionIcon>
                      </Tooltip>

                      <Tooltip label="Edit User">
                        <ActionIcon variant="light" color="orange" onClick={() => handleEditUser(userItem)}>
                          <IconEdit size={18} />
                        </ActionIcon>
                      </Tooltip>

                      <Tooltip label="Delete User">
                        <ActionIcon variant="light" color="red" onClick={() => handleDeleteUser(userItem)}>
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Empty State - No Results */}
      {!error && !isLoading && users.length > 0 && filteredUsers.length === 0 && (
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Stack align="center" gap="md">
            <IconSearch size={48} stroke={1.5} color="var(--mantine-color-gray-5)" />
            <Box ta="center">
              <Text size="lg" fw={600} mb="xs">
                No Users Found
              </Text>
              <Text size="sm" c="dimmed" mb="md">
                No users match your search or filter criteria.
              </Text>
              <Button
                variant="subtle"
                onClick={() => {
                  setSearchQuery('');
                  setRoleFilter(null);
                  setStatusFilter(null);
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Empty State - No Users */}
      {!error && !isLoading && users.length === 0 && (
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Stack align="center" gap="md">
            <IconUser size={48} stroke={1.5} color="var(--mantine-color-gray-5)" />
            <Box ta="center">
              <Text size="lg" fw={600} mb="xs">
                No Users Found
              </Text>
              <Text size="sm" c="dimmed" mb="md">
                Create your first user to get started.
              </Text>
              <Button leftSection={<IconPlus size={16} />} onClick={handleCreateUser}>
                Create First User
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          // React Query will auto-refetch due to mutation invalidation
        }}
      />

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={isEditModalOpen}
        userId={selectedUserIdForEdit}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUserIdForEdit(null);
        }}
        onSuccess={() => {
          setIsEditModalOpen(false);
          setSelectedUserIdForEdit(null);
          // React Query will auto-refetch due to mutation invalidation
        }}
      />

      {/* Delete User Modal */}
      <DeleteUserModal
        isOpen={isDeleteModalOpen}
        user={selectedUser}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
          // React Query will auto-refetch due to mutation invalidation
        }}
      />
    </Stack>
  );
};
